import React from "react";
import { X, Calendar, MapPin } from "lucide-react";
import { ContactItem } from "../../../../views/crm-pages/customers/types";

// Opened from a WhatsApp Flow message's new "Open form" button (see
// avatar.tsx) in place of the generic read-only interactive-options preview.
// There's no real WhatsApp Flow field schema anywhere in this backend yet
// (message_metadata is freeform — confirmed, nothing describes a flow's
// actual fields), so this renders a fixed, static mock of the form shown in
// the reference design rather than one driven by real per-message data.
// Pre-fills whatever this conversation's own contact already has on file
// (name/phone/email); "Preferred visit time" and "Address" have no backing
// field on a contact at all, so they stay empty. Every field is read-only —
// per direct instruction, this is a preview of what the customer saw, not a
// form the tenant can actually fill in or submit.

interface FormPreviewPanelProps {
  leadContact?: ContactItem | null;
  onClose: () => void;
}

const ReadOnlyField: React.FC<{
  label: string;
  required?: boolean;
  value?: string;
  placeholder: string;
  icon?: React.ReactNode;
}> = ({ label, required, value, placeholder, icon }) => (
  <div className="mb-3.5">
    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">
      {label}
      {required && <span className="text-rose-500">*</span>}
    </label>
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
      {icon}
      <span className={`text-sm truncate ${value ? "text-gray-700 dark:text-gray-200" : "text-gray-400 dark:text-gray-500"}`}>
        {value || placeholder}
      </span>
    </div>
  </div>
);

export const FormPreviewPanel: React.FC<FormPreviewPanelProps> = ({ leadContact, onClose }) => {
  const fullName = [leadContact?.first_name || leadContact?.firstName, leadContact?.last_name || leadContact?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    <div className="w-[280px] min-w-[240px] bg-white dark:bg-gray-900 border-l border-[#e8ecf4] dark:border-gray-800 flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 pt-3.5 pb-2.5 border-b border-slate-100 dark:border-gray-800">
        <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">Form Preview</h3>
        <button onClick={onClose} className="p-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 bg-transparent border-none cursor-pointer">
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="mb-4">
          <p className="text-sm font-bold text-gray-900 dark:text-gray-100">Form title</p>
          <p className="text-xs text-gray-400 dark:text-gray-500">form description</p>
        </div>

        <ReadOnlyField label="Full Name" required value={fullName} placeholder="Enter your full name" />
        <ReadOnlyField label="Phone Number" required value={leadContact?.phone} placeholder="+234-8989893800" />
        <ReadOnlyField label="Email" required value={leadContact?.email} placeholder="karen.castillo@mail.com" />
        <ReadOnlyField label="Preferred visit time" required placeholder="Select date and time" icon={<Calendar size={14} className="text-gray-400 dark:text-gray-500 shrink-0" />} />
        <ReadOnlyField label="Address" placeholder="Enter your Address" icon={<MapPin size={14} className="text-gray-400 dark:text-gray-500 shrink-0" />} />
      </div>

      <div className="p-4 border-t border-slate-100 dark:border-gray-800 space-y-2">
        <button
          type="button"
          className="w-full py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors"
        >
          Submit Form
        </button>
        <button
          type="button"
          onClick={onClose}
          className="w-full py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          Close Form
        </button>
      </div>
    </div>
  );
};

export default FormPreviewPanel;
